import React, { useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ViewStyle,
} from 'react-native';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { ResidentMobileHeaderWithMenu } from './ResidentMobileHeaderWithMenu';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

interface BaseResidentMobileScreenProps {
  /** The current screen name for navigation highlighting */
  screenName?: string;
  /** Navigation prop from React Navigation (optional - will use hook if not provided) */
  navigation?: NavigationProp<RootStackParamList>;
  /** Custom content to render in the main area */
  children?: React.ReactNode;
  /** Additional styles for the content container */
  contentContainerStyle?: ViewStyle;
  /** Whether scrolling is enabled (default: false) */
  scrollEnabled?: boolean;
  /** Custom logo press handler */
  onLogoPress?: () => void;
  /** Custom sign out handler (optional - uses default if not provided) */
  onSignOut?: () => void;
  /** Custom notification press handler */
  onNotificationPress?: () => void;
  /** Whether to show notification button */
  showNotification?: boolean;
  /** Use dark background matching Figma design */
  darkBackground?: boolean;
  /** Header variant for redesigned screens */
  headerVariant?: 'default' | 'gradient' | 'image';
}

/**
 * BaseResidentMobileScreen - Reusable base component for resident mobile screens
 *
 * This component provides:
 * - Consistent layout and styling with new Figma-based header
 * - Navigation handling via slide-down menu
 * - Sign out functionality
 * - Notification access
 */
export const BaseResidentMobileScreen: React.FC<BaseResidentMobileScreenProps> = React.memo(({
  screenName,
  navigation: propNavigation,
  children,
  contentContainerStyle,
  scrollEnabled = false,
  onLogoPress,
  onSignOut,
  onNotificationPress,
  showNotification = true,
  darkBackground = true,
  headerVariant = 'default',
}) => {
  const hookNavigation = useNavigation<NavigationProp<RootStackParamList>>();
  const navigation = propNavigation || hookNavigation;
  const styles = useThemedStyles(makeStyles);

  // Use custom hook for resident screen logic
  const {
    userState,
    screenMetadata,
    handleNavigate,
    handleLogoPress: defaultHandleLogoPress,
    handleSignOut: defaultHandleSignOut,
    handleNotificationPress: defaultHandleNotificationPress,
  } = useResidentScreen(screenName, navigation as any);

  // Override handlers if provided
  const finalLogoPress = useCallback(() => {
    if (onLogoPress) {
      onLogoPress();
    } else {
      defaultHandleLogoPress();
    }
  }, [onLogoPress, defaultHandleLogoPress]);

  const finalSignOut = useCallback(async () => {
    if (onSignOut) {
      await onSignOut();
    } else {
      await defaultHandleSignOut();
    }
  }, [onSignOut, defaultHandleSignOut]);

  const finalNotificationPress = useCallback(() => {
    if (onNotificationPress) {
      onNotificationPress();
    } else {
      defaultHandleNotificationPress();
    }
  }, [onNotificationPress, defaultHandleNotificationPress]);

  const isRedesign = headerVariant === 'gradient' || headerVariant === 'image';
  const containerStyle = isRedesign
    ? styles.containerTransparent
    : darkBackground
      ? styles.containerDark
      : styles.container;
  const contentStyle = isRedesign
    ? styles.contentTransparent
    : darkBackground
      ? styles.contentDark
      : styles.content;

  return (
    <View style={containerStyle}>
      <ResidentMobileHeaderWithMenu
        onLogoPress={finalLogoPress}
        onSignOut={finalSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={finalNotificationPress}
        currentPath={screenMetadata.path}
        currentTitle={screenMetadata.title}
        showNotification={showNotification}
        variant={headerVariant}
      />

      {scrollEnabled ? (
        <ScrollView
          style={contentStyle}
          contentContainerStyle={[styles.contentContainer, contentContainerStyle]}
          showsVerticalScrollIndicator={false}
          scrollEnabled={scrollEnabled}
        >
          {children}
        </ScrollView>
      ) : (
        <View style={[contentStyle, styles.contentContainer, contentContainerStyle]}>
          {children}
        </View>
      )}
    </View>
  );
});

BaseResidentMobileScreen.displayName = 'BaseResidentMobileScreen';

const makeStyles = ({ components }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: components.nav.background,
  },
  containerDark: {
    flex: 1,
    backgroundColor: components.nav.background,
  },
  containerTransparent: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
  },
  contentDark: {
    flex: 1,
    backgroundColor: components.nav.background,
  },
  contentTransparent: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    flexGrow: 1,
  },
});
